import React from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { key } from 'styled-theme';
import { IntlMessages } from '../../../helper/langHelper';

// Styled Components
const PaginationContainer = styled.nav`
  width: 100%;
  background-color: #ffffff;
  position: relative;
  border-radius: 4px;
  margin-bottom: 24px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: #7b5cff;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(props) => (props.disabled ? '#9ca3af' : '#2563eb')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition:
    background-color 0.2s,
    box-shadow 0.2s;

  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  &:hover:not(:disabled) {
    color: #1d4ed8;
    background-color: rgba(37, 99, 235, 0.05);
  }

  &:active:not(:disabled) {
    background-color: rgba(37, 99, 235, 0.1);
  }
`;

const ButtonText = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
`;

// Accessible status text that's visually hidden but available to screen readers
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

// Pagination Component
const Pagination = ({
  page = 1,
  pageSize = 10,
  prevPage = () => {},
  nextPage = () => {},
  total = 0,
}) => {
  const maxPages = Math.ceil(total / pageSize);
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  const hasPrev = page > 0;
  const hasNext = page + 1 < maxPages;

  return (
    <PaginationContainer aria-label="Pagination navigation">
      <PaginationWrapper>
        <PaginationInfo id="pagination-results" aria-live="polite">
          <IntlMessages id="pagination.page" />: {start}-{end} <IntlMessages id="pagination.of" />{' '}
          {total}
        </PaginationInfo>

        <ButtonsContainer>
          <PaginationButton
            onClick={hasPrev ? prevPage : undefined}
            disabled={!hasPrev}
            aria-disabled={!hasPrev}
            aria-label="Go to previous page"
            className="pagination_prev_button"
          >
            <ChevronLeft size={16} aria-hidden="true" color={hasPrev ? '#2563eb' : '#9ca3af'} />
            <ButtonText>
              <IntlMessages id="pagination.previous" />
            </ButtonText>
            {!hasPrev && <VisuallyHidden>(disabled)</VisuallyHidden>}
          </PaginationButton>

          <PaginationButton
            onClick={hasNext ? nextPage : undefined}
            disabled={!hasNext}
            aria-disabled={!hasNext}
            aria-label="Go to next page"
            className="pagination_next_button"
          >
            <ButtonText>
              <IntlMessages id="pagination.next" />
            </ButtonText>
            <ChevronRight size={16} aria-hidden="true" color={hasNext ? '#2563eb' : '#9ca3af'} />
            {!hasNext && <VisuallyHidden>(disabled)</VisuallyHidden>}
          </PaginationButton>
        </ButtonsContainer>
      </PaginationWrapper>
    </PaginationContainer>
  );
};

export default Pagination;
